import type { ClaimClosureOutcome, ClaimProductBinding, ClaimSource } from '@shared/prisma/enums';

/**
 * PostHog flag for deferred product binding (CUR-2160): recording a claim without a product and
 * choosing it later. Off by default and stacked on `PRODUCTS_V2_FLAG_KEY`; every new procedure
 * answers NOT_FOUND while either is off.
 */
export const CLAIM_DEFERRED_BINDING_FLAG_KEY = 'enable-claim-deferred-binding';

/**
 * The longest "What happened" a claim keeps (`Claim.additionalInfo`): the notice it was recorded
 * from, and every later edit on the claim's Manage page.
 */
export const CLAIM_DESCRIPTION_MAX_LENGTH = 5000;

/** The one refusal every product-dependent action gives a claim awaiting its product. */
export const CLAIM_PRODUCT_REQUIRED_MESSAGE =
    'Choose the product for this claim first. Reserves, payments and assessments need it.';

/**
 * How a claim reached the platform, as handlers read it
 * (docs/plan/claim-deferred-product-binding/plan.md § 5.1).
 */
export const CLAIM_SOURCE_LABELS = {
    LODGEMENT_FORM: 'Lodgement form',
    EMAIL: 'Email',
    DOCUMENTS: 'Documents',
    SERVICE_API: 'Service API',
    MANUAL: 'Recorded by staff',
    DEMO: 'Demo data',
} as const satisfies Record<ClaimSource, string>;

/** When a claim's product was decided, as handlers read it in History. */
export const CLAIM_PRODUCT_BINDING_LABELS = {
    AT_CREATION: 'Chosen at lodgement',
    PENDING: 'Awaiting product',
    BOUND: 'Chosen after lodgement',
} as const satisfies Record<ClaimProductBinding, string>;

/**
 * Why a claim was closed, as handlers read it (CUR-2167, plan § 5.1). Ordered as the close dialog
 * offers them: the outcomes of a claim first, then the exits of something that was never one.
 */
export const CLAIM_CLOSURE_OUTCOME_LABELS = {
    CLAIM_SETTLED: 'Settled',
    CLAIM_DECLINED: 'Declined',
    WITHDRAWN: 'Withdrawn',
    NOT_A_CLAIM: 'Not a claim',
    DUPLICATE: 'Duplicate',
} as const satisfies Record<ClaimClosureOutcome, string>;

/** What each outcome means, under its label in the close dialog. */
export const CLAIM_CLOSURE_OUTCOME_DESCRIPTIONS = {
    CLAIM_SETTLED: 'The claim was accepted and paid.',
    CLAIM_DECLINED: 'The claim was not covered.',
    WITHDRAWN: 'The notifier no longer wants to claim.',
    NOT_A_CLAIM: 'An enquiry, a quote request, or a notice that was not about a loss.',
    DUPLICATE: 'Another claim already covers this loss.',
} as const satisfies Record<ClaimClosureOutcome, string>;

/** Every outcome, in the dialog's order. */
export const CLAIM_CLOSURE_OUTCOMES = Object.keys(CLAIM_CLOSURE_OUTCOME_LABELS) as ClaimClosureOutcome[];

/**
 * The outcomes of something that was never a claim: such a claim leaves claim counts, dashboards
 * and reports, as one still awaiting its product does.
 */
export const NON_CLAIM_CLOSURE_OUTCOMES = [
    'NOT_A_CLAIM',
    'DUPLICATE',
] as const satisfies readonly ClaimClosureOutcome[];

/** A claim without a product was never assessed, so it can be neither settled nor declined. */
export const CLOSURE_OUTCOMES_WITHOUT_PRODUCT = [
    'NOT_A_CLAIM',
    'DUPLICATE',
    'WITHDRAWN',
] as const satisfies readonly ClaimClosureOutcome[];
